import type { CSSProperties, ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import { Button, I, Row } from '@leanin/ui';

// Tighter chip padding for the post-action row. The default Button size="sm"
// padding (6/12) is sized for label text; the chips here carry icon+number
// or icon+short-label, which look loose with the standard padding. Exported
// so PostCard's reactSlot Button picks up the identical shape.
export const chipPadding: CSSProperties = {
  padding: 'var(--space-xs) var(--space-sm)',
};

export function PostActions({
  likes,
  comments,
  commentsOpen,
  onLike,
  onComment,
  onSave,
  saved,
  reactSlot,
  shareSlot,
  extraSlot,
}: {
  likes?: number | string;
  comments?: number | string;
  commentsOpen?: boolean;
  onLike?: () => void;
  onComment?: () => void;
  onSave?: () => void;
  saved?: boolean;
  /** Replaces the default reaction chip — host renders a reaction-picker
   *  trigger here. */
  reactSlot?: ReactNode;
  /** Slot for a share trigger (typically `<ShareEntityButton entity={{kind:'post', id}} variant="ghost" size="sm" />`).
   *  Sits in the trailing icon cluster alongside save. Omit on view-only
   *  listings that have no real post identity to share. */
  shareSlot?: ReactNode;
  /** Extra trailing actions rendered before the share trigger — e.g. the
   *  resource page's Download PDF button. */
  extraSlot?: ReactNode;
}) {
  const t = useTranslations('Feed');
  const commentCountNum =
    typeof comments === 'number'
      ? comments
      : typeof comments === 'string'
        ? Number.parseInt(comments, 10) || 0
        : 0;

  return (
    <Row
      py="md"
      px="xl"
      align="center"
      justify="space-between"
      wrap
      style={{ borderTop: '1px solid var(--border)' }}
    >
      <Row gap="sm" align="center">
        {reactSlot ?? (
          <Button
            variant="secondary"
            size="sm"
            icon={I.heart}
            onClick={onLike}
            style={chipPadding}
            aria-label={t('actions.react')}
          >
            {likes ?? 0}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          icon={I.chat}
          onClick={onComment}
          style={chipPadding}
          aria-expanded={commentsOpen}
          aria-label={t('actions.comment')}
        >
          {t('actions.commentCount', { count: commentCountNum })}
        </Button>
      </Row>

      <Row gap="xs" align="center">
        {extraSlot}
        {shareSlot}
        {onSave !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            icon={saved === true ? I.bookmarkFilled : I.bookmark}
            onClick={onSave}
            style={saved === true ? { color: 'var(--primary)' } : undefined}
            aria-pressed={saved === true}
            aria-label={saved === true ? t('actions.savedAria') : t('actions.saveAria')}
          />
        ) : null}
      </Row>
    </Row>
  );
}
